import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useBlogPosts } from '../../hooks/useBlogPosts';
import MainNavigation from '../../components/layout/MainNavigation';
import './blog.css';

const BASE_URL = 'https://efortipsi.com.br';

const TITLE = 'Blog de Psicologia | Saúde Mental e Psicanálise - eFortiPsi';
const DESCRIPTION =
  'Artigos e reflexões sobre psicologia, saúde mental, psicanálise e desenvolvimento emocional escritos pela psicóloga Eliane Forti Fernandes (CRP 06/216637).';
const BLOG_URL = `${BASE_URL}/blog`;

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Início',
      item: BASE_URL,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Blog',
      item: BLOG_URL,
    },
  ],
};

const Blog: React.FC = () => {
  const { posts: blogPosts, loading } = useBlogPosts();

  return (
    <div className="min-h-screen bg-gray-50">
      <MainNavigation />
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <meta name="keywords" content="blog psicologia, saúde mental, psicanálise, ansiedade, desenvolvimento emocional, psicóloga caieiras" />
        <meta name="author" content="Eliane Forti Fernandes" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <link rel="canonical" href={BLOG_URL} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:image" content={`${BASE_URL}/og-image.jpg`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Blog eFortiPsi - Psicologia e Saúde Mental" />
        <meta property="og:url" content={BLOG_URL} />
        <meta property="og:site_name" content="eFortiPsi - Psicóloga Eliane Forti Fernandes" />
        <meta property="og:locale" content="pt_BR" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content={`${BASE_URL}/og-image.jpg`} />
        <meta name="twitter:image:alt" content="Blog eFortiPsi - Psicologia e Saúde Mental" />

        {/* Schema.org - BreadcrumbList */}
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Espaçamento para evitar sobreposição com o menu fixo */}
      <div className="pt-16"></div>

      {/* Header */}
      <header className="bg-gradient-to-r from-[#2a3c5c] to-[#4a6c9c] text-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">Blog eFortiPsi</h1>
            <p className="text-lg sm:text-xl max-w-3xl mx-auto">
              Reflexões, artigos e conteúdos sobre psicologia, saúde mental e desenvolvimento emocional.
            </p>
          </motion.div>
        </div>
      </header>

      {/* Blog Content */}
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
                <div className="h-48 bg-gray-200" />
                <div className="p-6 space-y-3">
                  <div className="h-3 bg-gray-200 rounded w-1/3" />
                  <div className="h-5 bg-gray-200 rounded w-full" />
                  <div className="h-4 bg-gray-200 rounded w-5/6" />
                </div>
              </div>
            ))}
          </div>
        )}
        {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: Number(post.id) * 0.1 }}
              className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
            >
              <Link to={`/blog/${post.slug}`}>
                <div className="h-48 overflow-hidden">
                  <img
                    src={post.coverImage}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <span>{new Date(post.date).toLocaleDateString('pt-BR')}</span>
                    <span className="mx-2">•</span>
                    <span>{post.author}</span>
                  </div>
                  <h2 className="text-xl font-semibold text-[#2a3c5c] mb-2 hover:text-[#4a6c9c] transition-colors duration-200">
                    {post.title}
                  </h2>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
        )}
      </main>

      {/* Back to Home Link */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
        <Link
          to="/"
          className="inline-flex items-center text-[#2a3c5c] hover:text-[#4a6c9c] transition-colors duration-200"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Voltar para a página inicial
        </Link>
      </div>
    </div>
  );
};

export default Blog;
